import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, Wallet as WalletIcon, PartyPopper } from "lucide-react";
import { enterTask } from "@/lib/afritask";
import { toast } from "sonner";

/**
 * Reusable "Enter round" dialog + insufficient-points fallback.
 *
 * Controlled from outside via `task` (open when non-null).
 * onClose: () => void
 * onEntered: (result) => void  fires after successful entry
 */
export default function EnterRoundDialog({ task, onClose, onEntered }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("confirm"); // confirm | success | short
  const [shortInfo, setShortInfo] = useState(null); // {needed, have}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!task) return null;

  const reset = () => {
    setStep("confirm");
    setShortInfo(null);
    setResult(null);
    setBusy(false);
  };

  const closeAll = () => {
    reset();
    onClose && onClose();
  };

  const confirm = async () => {
    try {
      setBusy(true);
      const res = await enterTask(task.task_id);
      setResult(res);
      setStep("success");
      onEntered && onEntered(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 402 &&
        detail &&
        detail.error === "insufficient_points"
      ) {
        setShortInfo({ needed: detail.needed, have: detail.have });
        setStep("short");
      } else {
        toast.error(
          typeof detail === "string" ? detail : "Failed to enter round"
        );
        closeAll();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && closeAll()}>
      <DialogContent data-testid="enter-round-dialog">
        {step === "confirm" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Confirm entry</DialogTitle>
              <DialogDescription>
                <span className="font-semibold text-neutral-900">
                  {task.entry_cost_points} points
                </span>{" "}
                will be deducted from your wallet. You&apos;ll be notified when the
                round closes and selection completes.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <p className="text-sm font-semibold">{task.title}</p>
              <div className="mt-2 flex items-center justify-between text-xs text-neutral-500">
                <span className="inline-flex items-center gap-1">
                  <Zap className="h-3.5 w-3.5" />
                  {task.entry_cost_points} pts entry
                </span>
                <span className="font-semibold text-emerald-700">
                  Up to {task.reward_points} pts
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={closeAll}
                data-testid="enter-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirm}
                disabled={busy}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="enter-confirm-btn"
              >
                {busy ? "Entering…" : `Enter for ${task.entry_cost_points} pts`}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "short" && shortInfo && (
          <>
            <DialogHeader>
              <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <WalletIcon className="h-5 w-5" />
              </div>
              <DialogTitle>Not enough Work Points</DialogTitle>
              <DialogDescription>
                You need{" "}
                <span className="font-semibold text-neutral-900">
                  {shortInfo.needed - shortInfo.have} more points
                </span>{" "}
                to enter this round. You have{" "}
                <span className="font-semibold text-neutral-900">
                  {shortInfo.have} pts
                </span>
                .
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={closeAll}>
                Not now
              </Button>
              <Button
                onClick={() => {
                  closeAll();
                  navigate("/app/wallet");
                }}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="topup-now-btn"
              >
                Top up now
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "success" && result && (
          <>
            <DialogHeader>
              <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <PartyPopper className="h-5 w-5" />
              </div>
              <DialogTitle>You&apos;re in!</DialogTitle>
              <DialogDescription>
                We&apos;ll notify you when the round closes and selection is
                complete. Your entry is locked in.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
              New balance:{" "}
              <span className="font-semibold text-emerald-900">
                {result.wallet.work_points.toLocaleString()} pts
              </span>
            </div>
            <DialogFooter>
              <Button
                onClick={() => {
                  closeAll();
                  navigate("/app/applications");
                }}
                variant="outline"
                data-testid="view-applications-btn"
              >
                View my applications
              </Button>
              <Button
                onClick={closeAll}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="continue-browsing-btn"
              >
                Keep browsing
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
